/**
 * CourseMaterialList — sortable list of materials for one CourseLesson.
 * Shows type icon, title, reorder handle, edit/delete actions.
 * Action bar: "Thêm liên kết" + "Tải lên tệp".
 */
import { useState } from 'react';
import {
  FileText,
  Link as LinkIcon,
  Music,
  Presentation,
  Video,
  Pencil,
  Trash2,
  Plus,
  Upload,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { CourseMaterialForm, type MaterialLinkFormValues } from '@client/components/forms/course-material-form';
import { CourseMaterialUploadModal } from './course-material-upload-modal';
import { DragHandle, SortableList } from './sortable-list';
import {
  useCourseMaterials,
  useCreateLinkMaterial,
  useUpdateMaterial,
  useDeleteMaterial,
  useReorderMaterials,
  useDownloadUrl,
  type CourseMaterial,
} from '@client/lib/queries/use-course-materials';

// ---------------------------------------------------------------------------
// Icon helpers
// ---------------------------------------------------------------------------
function MaterialIcon({ type }: { type: CourseMaterial['type'] }) {
  const cls = 'h-4 w-4 shrink-0';
  switch (type) {
    case 'video':    return <Video className={cls} />;
    case 'audio':    return <Music className={cls} />;
    case 'slide':    return <Presentation className={cls} />;
    case 'document': return <FileText className={cls} />;
    case 'link':     return <LinkIcon className={cls} />;
    default:         return <FileText className={cls} />;
  }
}

const TYPE_LABELS: Record<CourseMaterial['type'], string> = {
  file: 'Tệp',
  link: 'Liên kết',
  video: 'Video',
  audio: 'Audio',
  slide: 'Slide',
  document: 'Tài liệu',
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// Single material row (used inside SortableList renderItem)
// ---------------------------------------------------------------------------
interface MaterialRowProps {
  material: CourseMaterial & { _dragHandleProps?: React.HTMLAttributes<HTMLElement> };
  onEdit: (m: CourseMaterial) => void;
  onDelete: (m: CourseMaterial) => void;
}

function MaterialRow({ material, onEdit, onDelete }: MaterialRowProps) {
  const downloadUrl = useDownloadUrl();

  async function handleOpen() {
    if (material.fileKey) {
      // R2-backed file: get signed download URL
      try {
        const { url } = await downloadUrl.mutateAsync(material.id);
        window.open(url, '_blank', 'noopener,noreferrer');
      } catch {
        toast.error('Không tải được liên kết tải về.');
      }
    } else if (material.url) {
      window.open(material.url, '_blank', 'noopener,noreferrer');
    }
  }

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-card hover:bg-accent/30 transition-colors group">
      {/* Drag handle */}
      <DragHandle {...(material._dragHandleProps ?? {})} />

      {/* Type icon */}
      <span className="text-muted-foreground">
        <MaterialIcon type={material.type} />
      </span>

      {/* Title + meta */}
      <div className="flex-1 min-w-0">
        <button
          type="button"
          onClick={handleOpen}
          className="text-sm font-medium text-foreground hover:text-primary hover:underline truncate text-left flex items-center gap-1"
          disabled={downloadUrl.isPending}
        >
          {material.title}
          {downloadUrl.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-60" />
          )}
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="secondary" className="text-xs px-1.5 py-0">
            {TYPE_LABELS[material.type]}
          </Badge>
          {material.fileSize != null && (
            <span className="text-xs text-muted-foreground">{formatBytes(material.fileSize)}</span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => onEdit(material)}
          aria-label="Sửa"
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 hover:text-destructive"
          onClick={() => onDelete(material)}
          aria-label="Xóa"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------
interface CourseMaterialListProps {
  courseLessonId: string;
}

export function CourseMaterialList({ courseLessonId }: CourseMaterialListProps) {
  const { data: materials, isLoading, isError } = useCourseMaterials(courseLessonId);

  const createLink = useCreateLinkMaterial();
  const reorder = useReorderMaterials(courseLessonId);
  const deleteMaterial = useDeleteMaterial();

  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CourseMaterial | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CourseMaterial | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // For edit we reuse CourseMaterialForm but in update mode
  const updateMaterial = useUpdateMaterial(editTarget?.id ?? '');

  async function handleCreateLink(values: MaterialLinkFormValues) {
    try {
      await createLink.mutateAsync({
        courseLessonId,
        type: values.type,
        title: values.title,
        url: values.url,
        description: values.description || undefined,
      });
      toast.success('Đã thêm học liệu.');
      setLinkDialogOpen(false);
    } catch {
      toast.error('Không thêm được học liệu.');
    }
  }

  async function handleUpdateLink(values: MaterialLinkFormValues) {
    if (!editTarget) return;
    try {
      await updateMaterial.mutateAsync({
        type: values.type,
        title: values.title,
        url: values.url,
        description: values.description || undefined,
      });
      toast.success('Đã cập nhật học liệu.');
      setEditDialogOpen(false);
      setEditTarget(null);
    } catch {
      toast.error('Không cập nhật được học liệu.');
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteMaterial.mutateAsync(deleteTarget.id);
      toast.success('Đã xóa học liệu.');
    } catch {
      toast.error('Không xóa được học liệu.');
    } finally {
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  }

  function handleReorder(newItems: CourseMaterial[]) {
    const reorderPayload = newItems.map((m, idx) => ({ id: m.id, sortOrder: idx }));
    reorder.mutate(reorderPayload);
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 pl-4">
        {[1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive" className="ml-4">
        <AlertDescription>Không tải được học liệu.</AlertDescription>
      </Alert>
    );
  }

  const items = materials ?? [];

  return (
    <div className="flex flex-col gap-2">
      {/* Header */}
      <div className="flex items-center justify-between pl-1">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Học liệu ({items.length})
        </span>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={() => setLinkDialogOpen(true)}
          >
            <Plus className="h-3 w-3" /> Liên kết
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={() => setUploadModalOpen(true)}
          >
            <Upload className="h-3 w-3" /> Tải lên
          </Button>
        </div>
      </div>

      {/* List */}
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground pl-1 py-2">
          Chưa có học liệu. Thêm liên kết hoặc tải tệp lên.
        </p>
      ) : (
        <SortableList
          items={items}
          onReorder={handleReorder}
          renderItem={(item) => (
            <div className="mb-1">
              <MaterialRow
                material={item as CourseMaterial & { _dragHandleProps?: React.HTMLAttributes<HTMLElement> }}
                onEdit={(m) => { setEditTarget(m); setEditDialogOpen(true); }}
                onDelete={(m) => { setDeleteTarget(m); setDeleteDialogOpen(true); }}
              />
            </div>
          )}
        />
      )}

      {/* Add link dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Thêm liên kết học liệu</DialogTitle>
          </DialogHeader>
          <CourseMaterialForm
            isSubmitting={createLink.isPending}
            onSubmit={handleCreateLink}
            onCancel={() => setLinkDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit dialog (link/external only) */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sửa học liệu</DialogTitle>
          </DialogHeader>
          {editTarget && (
            <CourseMaterialForm
              defaultValues={{
                type: editTarget.type === 'file' ? 'document' : editTarget.type as MaterialLinkFormValues['type'],
                title: editTarget.title,
                url: editTarget.url ?? '',
                description: editTarget.description ?? '',
              }}
              isSubmitting={updateMaterial.isPending}
              onSubmit={handleUpdateLink}
              onCancel={() => { setEditDialogOpen(false); setEditTarget(null); }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-sm w-[calc(100vw-2rem)]">
          <DialogHeader>
            <DialogTitle>Xóa học liệu?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Bạn có chắc muốn xóa <strong>{deleteTarget?.title}</strong>? Hành động này không thể hoàn tác.
          </p>
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end mt-2">
            <Button variant="ghost" onClick={() => setDeleteDialogOpen(false)}>Hủy</Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMaterial.isPending}
            >
              {deleteMaterial.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Xóa'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload modal */}
      <CourseMaterialUploadModal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        courseLessonId={courseLessonId}
      />
    </div>
  );
}
